import logger from './logger';

type UomRow = {
  value: string;
  description: string;
};

/**
 * Fetches UOM pretty-name mappings from Hasura by querying
 * the product_uom table for value and description.
 */
export async function fetchUomMap(params: {
  hasuraName: string;
  token: string;
}): Promise<Record<string, string>> {
  const { hasuraName, token } = params;

  if (!hasuraName || !token) {
    logger.warn('fetchUomMap called without hasuraName or token', {
      hasuraName,
      hasToken: !!token,
    });
    return {};
  }

  const query = `
    query GetUoms {
      product_uom {
        value
        description
      }
    }
  `;

  const endpoint = `https://${hasuraName}.hasura.app/v1/graphql`;

  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ query }),
    });

    if (!res.ok) {
      const text = await res.text();
      logger.error('fetchUomMap HTTP error', {
        status: res.status,
        statusText: res.statusText,
        body: text,
      });
      return {};
    }

    const json = await res.json() as {
      data?: Record<string, any>;
      errors?: unknown[];
    };

    if (json.errors) {
      logger.error('fetchUomMap GraphQL errors', { errors: json.errors });
      return {};
    }

    // Try to extract rows from various possible response structures
    let rows: UomRow[] = [];
    
    if (json.data?.product_uom) {
      rows = json.data.product_uom;
    } else if (json.data?.productHeaders?.product_uom) {
      rows = json.data.productHeaders.product_uom;
    } else if (json.data?.productUom) {
      rows = json.data.productUom;
    } else {
      logger.warn('Could not find UOM data in response', { 
        dataKeys: Object.keys(json.data || {}),
      });
      return {};
    }

    const map: Record<string, string> = {};
    for (const row of rows) {
      if (row.value && row.description) {
        map[row.value] = row.description;
      }
    }
    
    return map;
  } catch (error) {
    logger.error('fetchUomMap failed', { error });
    return {};
  }
}

